import { addNotification } from '@/shared/states/notification';
import { Loader2, Sparkles } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  useBulkCreateTestingProceduresWithAI,
  useCreateControlDescriptionWithAI,
  useCreateTestingProceduresWithAI,
  useFetchControlsDataQuery,
  useRefineTestingProceduresWithAIv2,
} from './hooks';
import { useStepThreeAutoSave } from './hooks/useStepThreeAutoSave';
import { useFrameworkWizardStore } from './states';
import { ControlData } from './types';
import { Spinner } from '@nextui-org/react';

// Memoized AI Button Component
const AIButton = memo(
  ({
    children,
    variant = 'primary',
    onClick,
    disabled = false,
    isLoading = false,
    textLoading = false,
    loaderOnly = false,
  }: {
    children: string;
    variant?: 'primary' | 'secondary';
    onClick?: () => void;
    disabled?: boolean;
    isLoading?: boolean;
    textLoading?: boolean;
    loaderOnly?: boolean;
  }) => (
    <button
      className={`flex flex-row items-center gap-1.5 py-1 h-6 px-2 rounded-[6px] text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed 
        ${variant === 'primary'
          ? 'bg-[#FAFAFA] border border-[#006FEE] text-[#006FEE]'
          : 'bg-transparent text-[#006FEE]'
        }`}
      onClick={onClick}
      disabled={disabled || isLoading}
    >
      {isLoading ? (
        <>
          <Loader2 size={12} className="text-current animate-spin" />
          {!loaderOnly && children}
        </>
      ) : (
        <>
          <Sparkles size={12} className="text-current" />
          {children}
        </>
      )}
    </button>
  )
);

AIButton.displayName = 'AIButton';

// Memoized Control List Item
const ControlListItem = memo(
  ({
    control,
    isSelected,
    onSelect,
    disabled,
  }: {
    control: ControlData;
    isSelected: boolean;
    onSelect: (id: string) => void;
    disabled?: boolean;
  }) => (
    <div
      className={`flex flex-col justify-center items-center py-2 px-3 ${
        disabled 
          ? 'cursor-not-allowed' 
          : 'cursor-pointer hover:bg-[#F4F4F5]'
      } ${isSelected ? 'rounded-md' : 'bg-white'}`}
      style={
        isSelected
          ? { background: 'linear-gradient(270deg, #FFFFFF 0%, #E6F1FE 100%)' }
          : {}
      }
      onClick={() => {
        if (!disabled) {
          onSelect(control.id);
        }
      }}
    >
      <span
        className={`text-sm w-full ${isSelected ? 'text-[#006FEE] font-medium' : 'text-[#3F3F46]'
          }`}
      >
        {control.id}
      </span>
    </div>
  )
);

ControlListItem.displayName = 'ControlListItem';

// Memoized Editable Column
const EditableColumn = memo(
  ({
    title,
    value,
    placeholder,
    onAIClick,
    onTextChange,
    aiButtonText,
    aiButtonDisabled,
    aiButtonLoading,
  }: {
    title: string;
    value: string;
    placeholder: string;
    onAIClick: () => void;
    onTextChange: (value: string) => void;
    aiButtonText: string;
    aiButtonDisabled: boolean;
    aiButtonLoading: boolean;
  }) => (
    <div className="flex flex-col w-2/4 border-r border-[#E4E4E7] last:border-r-0">
      <div className="flex flex-row justify-between items-center py-2 px-3 bg-[#F9F7F7] border-b border-[#E4E4E7] flex-shrink-0 min-h-[42px]">
        <span className="text-xs font-semibold text-[#71717A]">{title}</span>
        <AIButton
          variant="secondary"
          onClick={onAIClick}
          disabled={aiButtonDisabled}
          isLoading={aiButtonLoading}
        >
          {aiButtonText}
        </AIButton>
      </div>
      <div className="flex flex-col flex-grow">
        <div className="flex flex-col justify-start items-start py-2 px-3 border-b border-[#E4E4E7] bg-white relative h-full">
          <textarea
            className="text-sm text-[#3F3F46] w-full h-full resize-none border-none outline-none bg-transparent overflow-y-auto"
            value={value}
            onChange={(e) => onTextChange(e.target.value)}
            placeholder={placeholder}
          />
        </div>
      </div>
    </div>
  )
);

EditableColumn.displayName = 'EditableColumn';

// Utility function for flexible field matching
type Field = { key: string; ui_key: string };
const findFieldValue = (
  item: Record<string, unknown>,
  field: Field
): unknown => {
  // Try exact matches first
  if (field.key in item && item[field.key] !== undefined)
    return item[field.key];
  if (field.ui_key in item && item[field.ui_key] !== undefined)
    return item[field.ui_key];

  // Try case-insensitive matches
  const itemKeys = Object.keys(item);

  // Look for case-insensitive match with key
  const keyMatch = itemKeys.find(
    (k) => k.toLowerCase() === field.key.toLowerCase()
  );
  if (keyMatch && item[keyMatch] !== undefined) return item[keyMatch];

  // Look for case-insensitive match with ui_key
  const uiKeyMatch = itemKeys.find(
    (k) => k.toLowerCase() === field.ui_key.toLowerCase()
  );
  if (uiKeyMatch && item[uiKeyMatch] !== undefined) return item[uiKeyMatch];

  return '';
};

// Custom hook for data transformation - USE JSON EXACTLY AS FETCHED
const useControlsData = (
  fetchedData: unknown,
  isSuccess: boolean,
  schemaFields: Field[]
): ControlData[] => {
  return useMemo(() => {
    if (!isSuccess || !Array.isArray(fetchedData)) {
      return [];
    }
    // Use the data EXACTLY as fetched, just add ID field for UI if needed
    return (fetchedData as Array<Record<string, unknown>>).map(
      (item, index) => {
        if (!('id' in item)) {
          const itemKeys = Object.keys(item);
          const firstKey = itemKeys[0];
          const idValue =
            firstKey && item[firstKey]
              ? String(item[firstKey])
              : `control_${index}`;
          return { ...(item as ControlData), id: idValue };
        }
        return item as ControlData;
      }
    );
  }, [fetchedData, isSuccess, schemaFields]);
};

// Function to clean controls data - just remove our internal ID
const cleanControlsDataForUpload = (
  controls: ControlData[],
  schemaFields: any[]
) => {
  return controls.map((control) => {
    // Only include keys that are present in schemaFields (by key)
    const cleaned: Record<string, any> = {};
    schemaFields.forEach((field: any) => {
      if (control.hasOwnProperty(field.ui_key)) {
        cleaned[field.ui_key] = control[field.ui_key];
      }
    });
    return cleaned;
  });
};

// Custom hook for AI operations - use the actual field names from JSON
const useAIOperations = (
  stepTwoData: any,
  frameworkName: string,
  selectedControlId: string,
  controlsData: ControlData[],
  updateStepThreeData: any,
  schemaFields: any[],
  setRefinePopover?: any
) => {
  const createWithAIMutation = useCreateTestingProceduresWithAI();
  const createControlDescriptionWithAIMutation =
    useCreateControlDescriptionWithAI();
  const refineWithAIMutation = useRefineTestingProceduresWithAIv2();
  const bulkCreateWithAIMutation = useBulkCreateTestingProceduresWithAI();

  // Function to find description field in the actual data using schemaFields
  const getControlDescription = (control: ControlData) => {
    const descriptionField = schemaFields.find(
      (f: any) =>
        f.key.toLowerCase().includes('description') ||
        f.ui_key.toLowerCase().includes('description')
    );
    if (!descriptionField) return '';
    return (
      control[descriptionField.key] || control[descriptionField.ui_key] || ''
    );
  };

  // Function to find testing procedures field in the actual data using schemaFields
  const getTestingProcedures = (control: ControlData | undefined) => {
    if (!control) return '';
    const proceduresField = schemaFields.find(
      (f: any) =>
        f.key.toLowerCase().includes('procedure') ||
        f.ui_key.toLowerCase().includes('procedure')

    );
    if (!proceduresField) return '';
    return (
      control[proceduresField.key] || control[proceduresField.ui_key] || ''
    );
  };

  // Function to update testing procedures - use the dynamic field name from schemaFields
  const setTestingProcedures = (control: ControlData, value: string) => {
    const updatedControl = { ...control };
    const proceduresField = schemaFields.find(
      (f: any) =>
        f.key.toLowerCase().includes('procedure') ||
        f.ui_key.toLowerCase().includes('procedure')
    );
    if (proceduresField) {
      if (proceduresField.key in updatedControl) {
        updatedControl[proceduresField.key] = value;
      } else if (proceduresField.ui_key in updatedControl) {
        updatedControl[proceduresField.ui_key] = value;
      }
    }
    return updatedControl;
  };

  // Function to update description - use the dynamic field name from schemaFields
  const setControlDescription = (control: ControlData, value: string) => {
    const updatedControl = { ...control };
    const descriptionField = schemaFields.find(
      (f: any) =>
        f.key.toLowerCase().includes('description') ||
        f.ui_key.toLowerCase().includes('description')
    );
    if (descriptionField) {
      if (descriptionField.key in updatedControl) {
        updatedControl[descriptionField.key] = value;
      } else if (descriptionField.ui_key in updatedControl) {
        updatedControl[descriptionField.ui_key] = value;
      }
    }
    return updatedControl;
  };

  // Get field names for display - find the actual field names in the data
  const getFieldNames = () => {
    if (controlsData.length === 0) {
      return {
        description: '',
        testingProcedures: '',
      };
    }
    const firstControl = controlsData[0];
    const keys = Object.keys(firstControl);
    // Find description field
    const descriptionKey =
      keys.find((key) => key.toLowerCase().includes('description')) || '';
    // Find testing procedures field
    const testingKey =
      keys.find(
        (key) =>
          key.toLowerCase().includes('testing') ||
          key.toLowerCase().includes('procedure')
      ) || '';
    return {
      description: descriptionKey,
      testingProcedures: testingKey,
    };
  };

  const handleCreateWithAI = useCallback(
    async (controlId?: string) => {
      if (!stepTwoData.frameworkId) {
        addNotification({
          type: 'error',
          message: 'Framework ID not found. Please go back to Step 2.',
        });
        return;
      }

      const targetControlId = controlId || selectedControlId;
      const control = controlsData.find((c) => c.id === targetControlId);

      if (!control) {
        addNotification({
          type: 'error',
          message: 'Control not found',
        });
        return;
      }

      createWithAIMutation.mutate(
        {
          frameworkId: stepTwoData.frameworkId,
          frameworkName: frameworkName,
          controlDescription: getControlDescription(control),
        },
        {
          onSuccess: (testingProcedures) => {
            const updatedControls = controlsData.map((c) =>
              c.id === targetControlId
                ? setTestingProcedures(c, testingProcedures)
                : c
            );
            updateStepThreeData({ controls: updatedControls });
          },
        }
      );
    },
    [
      stepTwoData.frameworkId,
      frameworkName,
      selectedControlId,
      controlsData,
      createWithAIMutation,
      updateStepThreeData,
      getControlDescription,
      setTestingProcedures,
    ]
  );

  const handleRefineDescriptionWithAI = useCallback(
    async (controlId?: string) => {
      if (!stepTwoData.frameworkId) {
        addNotification({
          type: 'error',
          message: 'Framework ID not found. Please go back to Step 2.',
        });
        return;
      }

      const targetControlId = controlId || selectedControlId;
      const control = controlsData.find((c) => c.id === targetControlId);

      if (!control) {
        addNotification({
          type: 'error',
          message: 'Control not found',
        });
        return;
      }

      const descriptionToRefine = getControlDescription(control);
      if (!descriptionToRefine?.trim()) {
        addNotification({
          type: 'error',
          message: 'No control description to refine. Please add one first.',
        });
        return;
      }

      // Use the new createControlDescriptionWithAIMutation for creation
      createControlDescriptionWithAIMutation.mutate(
        {
          frameworkId: stepTwoData.frameworkId,
          frameworkName: frameworkName,
          controlDescription: descriptionToRefine,
        },
        {
          onSuccess: (newDescription) => {
            // Instead of updating immediately, show the popover for confirmation
            setRefinePopover({
              isVisible: true,
              originalContent: descriptionToRefine,
              refinedContent: newDescription,
              title: 'Control Description',
              controlId: targetControlId,
              result: { control_description: newDescription },
              fieldKey: 'control_description',
            });
          },
        }
      );
    },
    [
      stepTwoData.frameworkId,
      frameworkName,
      selectedControlId,
      controlsData,
      createControlDescriptionWithAIMutation,
      updateStepThreeData,
      getControlDescription,
      setControlDescription,
    ]
  );

  const handleRefineTestingProcedureWithAI = useCallback(
    async (controlId?: string) => {
      if (!stepTwoData.frameworkId) {
        addNotification({
          type: 'error',
          message: 'Framework ID not found. Please go back to Step 2.',
        });
        return;
      }

      const targetControlId = controlId || selectedControlId;
      const control = controlsData.find((c) => c.id === targetControlId);

      if (!control || !getTestingProcedures(control)?.trim()) {
        addNotification({
          type: 'error',
          message: 'No testing procedure to refine. Please create one first.',
        });
        return;
      }

      // Use the new refineWithAIMutation for refinement (returns both fields)
      refineWithAIMutation.mutate(
        {
          frameworkId: stepTwoData.frameworkId,
          frameworkName: frameworkName,
          controlDescription: getControlDescription(control),
          testingProcedures: getTestingProcedures(control),
        },
        {
          onSuccess: (result) => {
            const control = controlsData.find((c) => c.id === targetControlId);
            if (!control) return;
            const originalDescription = getControlDescription(control);
            const originalTestingProcedures = getTestingProcedures(control);
            let originalContent = '';
            let refinedContent = '';
            let title = '';
            let fieldKey = '';
            if (result.testing_procedures !== undefined) {
              originalContent = originalTestingProcedures;
              refinedContent = result.testing_procedures;
              title = 'Testing Procedures';
              fieldKey = 'testing_procedures';
            } else if (result.control_description !== undefined) {
              originalContent = originalDescription;
              refinedContent = result.control_description;
              title = 'Control Description';
              fieldKey = 'control_description';
            }
            setRefinePopover({
              isVisible: true,
              originalContent,
              refinedContent,
              title,
              controlId: targetControlId,
              result,
              fieldKey,
            });
          },
        }
      );
    },
    [
      stepTwoData.frameworkId,
      frameworkName,
      selectedControlId,
      controlsData,
      refineWithAIMutation,
      updateStepThreeData,
      getControlDescription,
      getTestingProcedures,
      schemaFields,
    ]
  );

  const handleBulkCreateWithAI = useCallback(async () => {
    if (!stepTwoData.frameworkId) {
      addNotification({
        type: 'error',
        message: 'Framework ID not found.',
      });
      return;
    }

    const controlsWithoutProcedures = controlsData.filter(
      (c) => !getTestingProcedures(c)?.trim()
    );

    if (controlsWithoutProcedures.length === 0) {
      addNotification({
        type: 'info',
        message: 'All controls already have testing procedures',
      });
      return;
    }

    bulkCreateWithAIMutation.mutate(
      {
        frameworkId: stepTwoData.frameworkId,
        frameworkName: frameworkName,
        controls: controlsWithoutProcedures.map((c) => ({
          id: c.id,
          description: getControlDescription(c),
        })),
      },
      {
        onSuccess: (results) => {
          const updatedControls = [...controlsData];

          results.forEach((result) => {
            if (result.success) {
              const controlIndex = updatedControls.findIndex(
                (c) => c.id === result.controlId
              );
              if (controlIndex !== -1) {
                updatedControls[controlIndex] = setTestingProcedures(
                  updatedControls[controlIndex],
                  result.testingProcedures || ''
                );
              }
            }
          });

          updateStepThreeData({ controls: updatedControls });
        },
      }
    );
  }, [
    stepTwoData.frameworkId,
    frameworkName,
    controlsData,
    bulkCreateWithAIMutation,
    updateStepThreeData,
    getControlDescription,
    getTestingProcedures,
    setTestingProcedures,
  ]);

  return {
    handleCreateWithAI,
    handleRefineDescriptionWithAI,
    handleRefineTestingProcedureWithAI,
    handleBulkCreateWithAI,
    getControlDescription,
    getTestingProcedures,
    setControlDescription: (controlId: string, value: string) => {
      const updatedControls = controlsData.map((control) =>
        control.id === controlId
          ? setControlDescription(control, value)
          : control
      );
      updateStepThreeData({ controls: updatedControls });
    },
    setTestingProcedures: (controlId: string, value: string) => {
      const updatedControls = controlsData.map((control) =>
        control.id === controlId
          ? setTestingProcedures(control, value)
          : control
      );
      updateStepThreeData({ controls: updatedControls });
    },
    mutations: {
      createWithAIMutation,
      refineWithAIMutation,
      refineDescriptionWithAIMutation: createControlDescriptionWithAIMutation,
      bulkCreateWithAIMutation,
    },
    fieldNames: getFieldNames(),
  };
};

export default function StepThree({
  setProceedBlocked,
}: { setProceedBlocked?: (blocked: boolean) => void } = {}) {
  const [selectedControlId, setSelectedControlId] = useState<string>('');
  const [previousControlId, setPreviousControlId] = useState<string>('');
  const [ignoreMissingProcedures, setIgnoreMissingProcedures] = useState(false);

  // Popover state for refine with AI
  const [refinePopover, setRefinePopover] = useState<{
    isVisible: boolean;
    originalContent: string;
    refinedContent: string;
    title: string;
    controlId: string;
    result: any;
    fieldKey: string;
  }>({
    isVisible: false,
    originalContent: '',
    refinedContent: '',
    title: '',
    controlId: '',
    result: null,
    fieldKey: '',
  });
  // Ref to anchor the popover to the correct button
  const popoverAnchorRef = useRef<HTMLSpanElement | null>(null);

  // Popover position state
  const [popoverPosition, setPopoverPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  // Update popover position when it opens or anchor changes
  useEffect(() => {
    if (refinePopover.isVisible) {
      // Always position relative to the control_description button, regardless of which field triggered it
      const controlDescriptionButton = document.querySelector('[data-field="control_description"]');
      const anchorElement = controlDescriptionButton || popoverAnchorRef.current;
      if (anchorElement) {
        const rect = anchorElement.getBoundingClientRect();
        const popoverWidth = 564;
        const margin = 16;
        const verticalGap = 16;
        
        // Always use control_description button position for consistent placement
        const left = rect.right + window.scrollX - popoverWidth - (refinePopover.fieldKey !== 'control_description' ? 0 : 0);
        let top = rect.bottom + window.scrollY + verticalGap;

        // Clamp left so popover doesn't overflow left edge
        const finalLeft = Math.max(left, margin);
        
        // If popover would overflow right edge, clamp to window's right margin
        const maxLeft = window.innerWidth - popoverWidth - margin;
        const clampedLeft = Math.min(finalLeft, maxLeft);
        
        // For vertical positioning, just ensure it doesn't go off screen
        const minTop = margin;
        const finalTop = Math.max(top, minTop);
        
        setPopoverPosition({ top: finalTop, left: clampedLeft });
      }
    } else {
      setPopoverPosition(null);
    }
  }, [
    refinePopover.isVisible,
    refinePopover.fieldKey,
  ]);

  const { stepTwoData, stepThreeData, updateStepThreeData, frameworkName } =
    useFrameworkWizardStore();

  const {
    data: fetchedControlsData,
    isSuccess,
    isLoading,
    error,
  } = useFetchControlsDataQuery(
    stepTwoData.controlsUrl,
    !!stepTwoData.controlsUrl
  );

  // Restore the auto-save hook - now it will use the backend URL directly
  const { saveNow, isSaving } = useStepThreeAutoSave();

  // Get schema fields from stepTwoData instead of fetching separately
  const schemaFields = useMemo(() => {
    return stepTwoData.schemaFields || [];
  }, [stepTwoData.schemaFields]);

  // Use custom hook for data transformation with dynamic fields
  const transformedControls = useControlsData(
    fetchedControlsData,
    isSuccess,
    schemaFields
  );

  // Memoized controls data - use transformed controls if available, otherwise use stored data
  const controlsData = useMemo(() => {
    const data = stepThreeData.controls || [];
    return data;
  }, [stepThreeData.controls]);

  // Memoized selected control
  const selectedControl = useMemo(
    () => controlsData.find((control) => control.id === selectedControlId),
    [controlsData, selectedControlId]
  );

  // After controlsData is defined and memoized
  useEffect(() => {
    if (!selectedControlId && controlsData.length > 0) {
      const firstId = controlsData[0].id;
      if (typeof firstId === 'string') {
        setSelectedControlId(firstId);
      }
    }
  }, [selectedControlId, controlsData]);

  // Find the control_description field key
  const controlDescriptionField = useMemo(() => {
    return schemaFields.find(
      (f) =>
        f.key.toLowerCase().includes('description') ||
        f.ui_key.toLowerCase().includes('description')
    );
  }, [schemaFields]);

  // Check if any control is missing a description
  const isAnyDescriptionEmpty = useMemo(() => {
    if (!controlDescriptionField) return true;
    return controlsData.some(
      (c) =>
        !c[controlDescriptionField.key] && !c[controlDescriptionField.ui_key]
    );
  }, [controlsData, controlDescriptionField]);

  // Use custom hook for AI operations with dynamic fields
  const {
    handleCreateWithAI,
    handleRefineDescriptionWithAI,
    handleRefineTestingProcedureWithAI,
    handleBulkCreateWithAI,
    getControlDescription,
    getTestingProcedures,
    setControlDescription,
    setTestingProcedures,
    mutations,
    fieldNames,
  } = useAIOperations(
    stepTwoData,
    frameworkName,
    selectedControlId,
    controlsData,
    updateStepThreeData,
    schemaFields,
    setRefinePopover
  );

  // Popover handlers
  const handleAcceptRefinedContent = useCallback(() => {
    if (!refinePopover.result || !refinePopover.controlId) return;
    
    const updatedControls = controlsData.map((c) => {
      if (c.id !== refinePopover.controlId) return c;
      
      const updatedControl = { ...c };
      
      // Update control description if present
      if (refinePopover.result.control_description !== undefined) {
        const descriptionField = schemaFields.find(
          (f) =>
            f.key.toLowerCase().includes('description') ||
            f.ui_key.toLowerCase().includes('description')
        );
        if (descriptionField) {
          updatedControl[descriptionField.ui_key] = refinePopover.result.control_description;
        }
      }
      
      // Update testing procedures if present
      if (refinePopover.result.testing_procedures !== undefined) {
        const proceduresField = schemaFields.find(
          (f) =>
            f.key.toLowerCase().includes('procedure') ||
            f.ui_key.toLowerCase().includes('procedure')
        );
        if (proceduresField) {
          updatedControl[proceduresField.ui_key] = refinePopover.result.testing_procedures;
        }
      }
      
      return updatedControl;
    });
    
    updateStepThreeData({ controls: updatedControls });
    setRefinePopover((prev) => ({ ...prev, isVisible: false, fieldKey: '' }));
    popoverAnchorRef.current = null;
  }, [
    refinePopover,
    controlsData,
    schemaFields,
    updateStepThreeData,
  ]);

  const handleDiscardRefinedContent = useCallback(() => {
    setRefinePopover((prev) => ({ ...prev, isVisible: false, fieldKey: '' }));
    popoverAnchorRef.current = null;
  }, []);

  // Check if any AI mutation is in progress (move after mutations is declared)
  const isAnyAIMutationPending =
    mutations.createWithAIMutation.isPending ||
    mutations.bulkCreateWithAIMutation.isPending ||
    mutations.refineDescriptionWithAIMutation.isPending ||
    mutations.refineWithAIMutation.isPending;

  // All buttons should be disabled when refine popover is visible
  const areButtonsDisabled = refinePopover.isVisible || isAnyAIMutationPending || !stepTwoData.frameworkId;

  // Call setProceedBlocked if provided
  useEffect(() => {
    if (setProceedBlocked) {
      setProceedBlocked(isAnyDescriptionEmpty || isAnyAIMutationPending || refinePopover.isVisible);
    }
  }, [isAnyDescriptionEmpty, isAnyAIMutationPending, refinePopover.isVisible, setProceedBlocked]);

  // Memoized missing procedures count - only calculate if we have testing procedures
  const missingProceduresCount = useMemo(
    () => {
      // Check if we have testing procedures field in schema
      const hasProceduresField = schemaFields.some(
        (f) => f.key === 'testing_procedures' || f.key.toLowerCase().includes('procedure')
      );
      
      if (!hasProceduresField) return 0;
      return controlsData.filter((c) => !getTestingProcedures(c)).length;
    },
    [controlsData, getTestingProcedures, schemaFields]
  );

  // Update controls data when fetch is successful - FIXED LOGIC
  useEffect(() => {
    if (
      isSuccess &&
      transformedControls.length > 0 &&
      (!stepThreeData.controls || stepThreeData.controls.length === 0)
    ) {
      // Always update with fresh data from the server
      updateStepThreeData({ controls: transformedControls });

      // Set first control as selected if none is selected
      if (!selectedControlId && transformedControls.length > 0) {
        const firstId = transformedControls[0].id;
        if (typeof firstId === 'string') {
          setSelectedControlId(firstId);
        }
      }
    }
  }, [
    isSuccess,
    transformedControls,
    selectedControlId,
    updateStepThreeData,
    fetchedControlsData,
    schemaFields,
    stepThreeData.controls,
  ]);

  // Memoized change handlers - restore the saveNow calls
  const handleControlSelect = useCallback(
    async (controlId: string) => {
      // Don't do anything if selecting the same control
      if (controlId === selectedControlId) return;

      // If we have a previous control, save its data before switching
      if (
        selectedControlId &&
        stepThreeData.controls &&
        stepThreeData.controls.length > 0
      ) {
        try {
          // Trigger an immediate save of the current control's data
          await saveNow();
        } catch (err) {
          console.error('Error saving control data before switching:', err);
        }
      }

      setPreviousControlId(selectedControlId);
      setSelectedControlId(controlId);
    },
    [selectedControlId, stepThreeData.controls, saveNow]
  );

  // Export the clean function for use in the main wizard
  const getCleanedControlsData = useCallback(() => {
    return cleanControlsDataForUpload(controlsData, schemaFields);
  }, [controlsData, schemaFields]);

  // Expose the cleaned data function through the store
  useEffect(() => {
    updateStepThreeData({
      controls: controlsData,
      getCleanedControls: getCleanedControlsData,
    });
  }, [controlsData, getCleanedControlsData, updateStepThreeData]);

  // Show loading state if we're fetching data
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4">
        <div className="text-sm text-[#71717A]"><Spinner size='md'/></div>
      </div>
    );
  }

  // Show error state if fetch failed
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4">
        <div className="text-sm text-red-500">Failed to load controls data</div>
        <div className="text-xs text-[#71717A]">{error.message}</div>
      </div>
    );
  }

  // Show loading state if schema fields are not available yet
  if (!schemaFields || schemaFields.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4">
        <div className="text-sm text-[#71717A]">
          Loading schema information...
        </div>
        <div className="text-xs text-[#71717A]">
          Controls URL: {stepTwoData.controlsUrl || 'Not found'}
        </div>
      </div>
    );
  }

  // Show message if no controls data
  if (controlsData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-full gap-4">
        <div className="text-sm text-[#71717A]">No controls data found</div>
        <div className="text-xs text-[#71717A]">
          {stepTwoData.controlsUrl
            ? `URL: ${stepTwoData.controlsUrl}`
            : 'No controls URL available'}
        </div>
        <div className="text-xs text-[#71717A]">
          Fetch success: {isSuccess ? 'Yes' : 'No'}, Raw data:{' '}
          {fetchedControlsData ? `${fetchedControlsData.length} items` : 'None'}
        </div>
      </div>
    );
  }

  // 1. Only allow control_description and testing_procedures for editable columns:
  const allowedKeys = ['control_description', 'testing_procedures'];
  const filteredFields = schemaFields.filter((f) =>
    allowedKeys.includes(f.key)
  );
  
  // Check if testing procedures field exists in schema
  const hasTestingProcedures = schemaFields.some(
    (f) => f.key === 'testing_procedures' || f.key.toLowerCase().includes('procedure')
  );
  
  // 2. Find the control_id field for the dynamic header:
  const controlIdField = schemaFields.find((f) => f.key === 'control_id');
  const controlIdHeader = controlIdField ? controlIdField.ui_key : 'Control ID';

  return (
    <div className="flex flex-col items-start self-stretch flex-grow w-full h-full gap-4">
      {/* Header Section */}
      <div className="flex flex-row items-center justify-between w-full gap-2 px-0 py-2">
        {/* Left: Title */}
        <span className="text-[14px] font-semibold text-[#18181B] leading-5 font-inter">
          Refine controls {hasTestingProcedures ? "and testing procedures " : ""}(optional).
        </span>
        {/* Right: Status and Buttons */}
        <div className="flex flex-row items-center gap-3">
          {hasTestingProcedures && !ignoreMissingProcedures && (
            <>
              <span className="text-[12px] font-normal text-[#F5A524] leading-5 font-inter mr-2">
                Testing procedures missing for {missingProceduresCount} of{' '}
                {controlsData.length} controls.
              </span>

              <AIButton
                textLoading={true}
                onClick={handleBulkCreateWithAI}
                disabled={areButtonsDisabled}
                isLoading={mutations.bulkCreateWithAIMutation.isPending}
              >
                Create with AI
              </AIButton>
              <button
                className="flex items-center justify-center px-2 py-1 gap-1.5 min-w-[53px] h-6 bg-[#FAFAFA] border border-[#006FEE] rounded-[6px] text-[#006FEE] text-xs font-medium leading-4 disabled:opacity-50"
                style={{
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '16px',
                }}
                disabled={areButtonsDisabled}
                onClick={() => setIgnoreMissingProcedures(true)}
              >
                Ignore
              </button>
            </>
          )}
        </div>
      </div>
      {/* Controls Table */}
      <div className="box-border flex flex-row w-full flex-grow bg-white border border-[#E4E4E7] rounded-lg overflow-hidden">
        {/* Controls List */}

        {/* Dynamic Columns for each schema field except id/Control ID */}
        <div className="flex flex-col border-r border-[#E4E4E7] mb-16">
          <div className="flex flex-row justify-between items-center py-2 px-3 bg-[#F9F7F7] border-b border-[#E4E4E7] flex-shrink-0 min-h-[42px]">
            <span className="text-xs font-semibold text-[#71717A]">
              {controlIdHeader}
            </span>
            {/* Invisible placeholder for button alignment */}
            <span className="w-16" />
          </div>
          <div className="flex flex-col overflow-y-auto w-[240px]">
            {controlsData.map((control) => (
              <ControlListItem
                key={control.id}
                control={control}
                isSelected={selectedControlId === control.id}
                onSelect={handleControlSelect}
                disabled={refinePopover.isVisible}
              />
            ))}
          </div>
        </div>
        {filteredFields.map((field: any) => {
          const isRefinePopoverOpen =
            refinePopover.isVisible &&
            refinePopover.controlId === selectedControlId &&
            refinePopover.fieldKey === field.key;
          
          const isTestingProceduresField = field.key.toLowerCase().includes('testing_procedures') || field.key.toLowerCase().includes('procedure');
          const isDescriptionField = field.key.toLowerCase().includes('description');
          
          // Skip this field if it's testing procedures and we don't have testing procedures in schema
          if (isTestingProceduresField && !hasTestingProcedures) {
            return null;
          }
          
          // Determine if this specific field is loading
          const isFieldLoading = isTestingProceduresField 
            ? (mutations.createWithAIMutation.isPending && selectedControlId === selectedControl?.id) ||
              (mutations.refineWithAIMutation.isPending && selectedControlId === selectedControl?.id)
            : isDescriptionField
              ? mutations.refineDescriptionWithAIMutation.isPending && selectedControlId === selectedControl?.id
              : false;
          
          // Determine width based on whether we have testing procedures
          const columnWidth = hasTestingProcedures ? "w-2/4" : "w-full";
          
          return (
            <div
              key={field.key}
              className={`relative flex flex-col ${columnWidth} border-r border-[#E4E4E7] last:border-r-0`}
            >
              {/* Header and AI Button Row */}
              <div className="flex flex-row justify-between items-center py-2 px-3 bg-[#F9F7F7] border-b border-[#E4E4E7] flex-shrink-0 min-h-[42px]">
                <span className="text-xs font-semibold text-[#71717A]">
                  {field.ui_key}  {isTestingProceduresField && '(optional)'}
                </span>
                <span
                  ref={(el) => {
                    if (isRefinePopoverOpen)
                      popoverAnchorRef.current = el as HTMLSpanElement;
                  }}
                  data-field={field.key}
                  style={{ display: 'inline-block' }}
                >
                  <AIButton
                    variant="secondary"
                    onClick={() => {
                      if (isTestingProceduresField) {
                        const value = selectedControl
                          ? selectedControl[field.key] ||
                          selectedControl[field.ui_key] ||
                          ''
                          : '';
                        if (value && value.trim()) {
                          handleRefineTestingProcedureWithAI(selectedControlId);
                        } else {
                          handleCreateWithAI(selectedControlId);
                        }
                      } else if (isDescriptionField) {
                        handleRefineDescriptionWithAI(selectedControlId);
                      }
                    }}
                    disabled={areButtonsDisabled}
                    isLoading={isFieldLoading}
                    loaderOnly={isFieldLoading}
                  >
                    {isTestingProceduresField
                      ? getTestingProcedures(selectedControl)?.trim()
                        ? 'Refine with AI'
                        : 'Create with AI'
                      : isDescriptionField
                        ? 'Refine with AI'
                        : ''}
                  </AIButton>
                </span>
              </div>
              {/* Textarea for editing value */}
              <div className="flex flex-col flex-grow">
                <div className="flex flex-col justify-start items-start py-2 px-3 border-b border-[#E4E4E7] bg-white relative h-[calc(100vh-210px)]">
                  <textarea
                    key={`${selectedControlId}-${field.ui_key}`} // Force re-render when control changes
                    className="text-sm text-[#3F3F46] w-full h-full resize-none border-none outline-none bg-transparent overflow-y-auto"
                    value={
                      selectedControl
                        ? selectedControl[field.key] ||
                        selectedControl[field.ui_key] ||
                        ''
                        : ''
                    }
                    onChange={(e) => {
                      const updatedControls = (
                        stepThreeData.controls || []
                      ).map((control) => {
                        if (control.id !== selectedControlId) return control;
                        return {
                          ...control,
                          [field.ui_key]: e.target.value.trimStart(), // Always update the ui_key (display/backend key)
                        };
                      });
                      updateStepThreeData({ controls: updatedControls });
                    }}
                    placeholder={`Enter ${field.ui_key.toLowerCase()}...`}
                    disabled={isFieldLoading || refinePopover.isVisible} // Disable during AI operations or when popover is visible
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {refinePopover.isVisible &&
        popoverPosition &&
        createPortal(
          <>
            {/* Overlay to block interactions with underlying interface */}
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                // backgroundColor: 'rgba(0, 0, 0, 0.3)',
                zIndex: 9998,
                // backdropFilter: 'blur(2px)',
              }}
            />
            {/* Refine Popup */}
            <div
              style={{
                position: 'absolute',
                top: popoverPosition.top,
                left: popoverPosition.left,
                zIndex: 9999,
                width: 564,
                minHeight: 136,
                maxHeight: 'calc(100vh - 100px)',
                padding: 12,
                gap: 12,
                backgroundImage: `
                  linear-gradient(#fff, #fff),
                  linear-gradient(180deg, #FF6496 0%, #812DD6 100%)
                `,
                backgroundOrigin: 'border-box',
                backgroundClip: 'padding-box, border-box',
                boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.15)',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                border: '1px solid transparent',
                borderRadius: 16,
              }}
            >
              {/* Header Row */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  padding: 0,
                  gap: 24,
                  width: 540,
                  height: 20,
                  marginBottom: 12,
                }}
              >
              {/* Title */}
              <div
                style={{
                  height: 20,
                  fontFamily: 'Inter',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: 14,
                  lineHeight: '20px',
                  color: '#18181B',
                  flex: 'none',
                  order: 0,
                  flexGrow: 0,
                }}
              >
                Accept refined version?
              </div>
              {/* Buttons */}
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: 0,
                  gap: 12,
                  width: 133,
                  height: 20,
                  flex: 'none',
                  order: 1,
                  flexGrow: 0,
                }}
              >
                {/* Accept Button */}
                <button
                  onClick={handleAcceptRefinedContent}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 0,
                    gap: 6,
                    width: 59,
                    height: 20,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: 12,
                    color: '#18181B',
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 6.5L5.5 9L9 4"
                        stroke="#18181B"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span
                    style={{
                      width: 41,
                      height: 20,
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '20px',
                      color: '#18181B',
                      marginLeft: 2,
                    }}
                  >
                    Accept
                  </span>
                </button>
                {/* Discard Button */}
                <button
                  onClick={handleDiscardRefinedContent}
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 0,
                    gap: 6,
                    width: 62,
                    height: 20,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Inter',
                    fontWeight: 500,
                    fontSize: 12,
                    color: '#18181B',
                  }}
                >
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 3L3 9"
                        stroke="#18181B"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 3L9 9"
                        stroke="#18181B"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span
                    style={{
                      width: 44,
                      height: 20,
                      fontFamily: 'Inter',
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: '20px',
                      color: '#18181B',
                      marginLeft: 2,
                    }}
                  >
                    Discard
                  </span>
                </button>
              </div>
            </div>
            {/* Content */}
            <div
              style={{
                width: 540,
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '20px',
                color: '#3F3F46',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: 'calc(100vh - 300px)',
                overflowY: 'auto',
                whiteSpace: "pre-wrap",
                wordWrap: 'break-word',
                padding: '8px 0'
              }}
            >
              {refinePopover.refinedContent || refinePopover.originalContent}
            </div>
          </div>
          </>,
          document.body
        )}
    </div>
  );
}
